import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import ExperienceCard from "../Cards/ExperienceCard";
import { experiences } from "../../data/constants";
import { useTheme } from "styled-components";

// Subtle animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const gentlePulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 80px 20px;
  background: ${({ theme }) => theme.bg};

  @media (max-width: 768px) {
    padding: 60px 16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  gap: 24px;
  animation: ${fadeIn} 0.8s ease;
`;

const Title = styled.h2`
  font-size: 42px;
  font-weight: 700;
  text-align: center;
  background: linear-gradient(135deg, #854ce6, #5ab2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  padding-bottom: 16px;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #854ce6, #5ab2ff);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Desc = styled.p`
  font-size: 18px;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
  color: ${({ theme }) => theme.text_secondary};

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TimelineSection = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 50px auto 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledTimeline = styled(Timeline)`
  padding: 0 !important;
  margin: 0 !important;

  & .MuiTimelineItem-root:before {
    flex: 0;
    padding: 0;
  }
`;

const StyledTimelineItem = styled(TimelineItem)`
  min-height: 180px;
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    min-height: 160px;
    margin-bottom: 20px;
  }
`;

const StyledTimelineDot = styled(TimelineDot)`
  border: 2px solid #854ce6 !important;
  background: rgba(133, 76, 230, 0.1) !important;
  padding: 8px !important;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(133, 76, 230, 0.2) !important;
    animation: ${gentlePulse} 1s ease;
  }
`;

const StyledConnector = styled(TimelineConnector)`
  background: linear-gradient(
    180deg,
    rgba(133, 76, 230, 0.4),
    rgba(90, 178, 255, 0.4)
  ) !important;
  width: 2px !important;
`;

const TimelineContentWrapper = styled(TimelineContent)`
  padding: 16px 20px !important;

  @media (max-width: 768px) {
    padding: 12px 16px !important;
  }
`;

const ExperienceCardWrapper = styled.div`
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(133, 76, 230, 0.15);
  }
`;

const DecorativeElement = styled.div`
  position: absolute;
  top: 20%;
  right: 10%;
  width: 200px;
  height: 200px;
  background: linear-gradient(
    135deg,
    rgba(133, 76, 230, 0.05),
    rgba(90, 178, 255, 0.05)
  );
  border-radius: 50%;
  filter: blur(40px);
  z-index: 0;
  opacity: 0.5;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Experience = () => {
  const theme = useTheme();
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container id="experience">
      <DecorativeElement />
      <Wrapper style={{ opacity: animated ? 1 : 0 }}>
        <Title>Experience</Title>
        <Desc>
          My journey as a software engineer, working with different companies
          and projects, gaining valuable skills and experiences along the way.
        </Desc>
        <TimelineSection>
          <StyledTimeline>
            {experiences.map((experience, index) => (
              <StyledTimelineItem key={index}>
                <TimelineSeparator>
                  <StyledTimelineDot />
                  {index !== experiences.length - 1 && <StyledConnector />}
                </TimelineSeparator>
                <TimelineContentWrapper>
                  <ExperienceCardWrapper>
                    <ExperienceCard experience={experience} />
                  </ExperienceCardWrapper>
                </TimelineContentWrapper>
              </StyledTimelineItem>
            ))}
          </StyledTimeline>
        </TimelineSection>
      </Wrapper>
    </Container>
  );
};

export default Experience;
